import React, { useState } from 'react';
import styled from 'styled-components';
import yaml from 'js-yaml';
import SdkGeneratorModal from './SdkGeneratorModal';

const Layout = styled.div`
  display: flex;
  height: 100%;
  background: ${props => props.$darkMode ? '#0d1117' : '#f6f8fa'};
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Noto Sans', Helvetica, Arial, sans-serif;
`;

const Sidebar = styled.div`
  width: ${props => props.$width}px;
  min-width: 200px;
  max-width: 600px;
  background: ${props => props.$darkMode ? '#0d1117' : '#fff'};
  border-right: 1px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
  overflow-y: auto;
  padding: 24px 16px;
  position: relative;
  box-shadow: ${props => props.$darkMode ? 'none' : '2px 0 8px rgba(0,0,0,0.04)'};
`;

const ResizeHandle = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  width: 4px;
  cursor: col-resize;
  background: transparent;
  transition: background 0.2s;
  
  &:hover {
    background: #0969da;
  }
  
  &:active {
    background: #0969da;
  }
`;

const Main = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 32px 48px;
  max-width: 1400px;
  margin: 0 auto;
`;

const Search = styled.input`
  width: 100%;
  padding: 10px 12px;
  background: ${props => props.$darkMode ? '#161b22' : '#f6f8fa'};
  border: 1px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
  border-radius: 8px;
  color: ${props => props.$darkMode ? '#e6edf3' : '#24292f'};
  margin-bottom: 24px;
  box-sizing: border-box;
  font-size: 14px;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #0969da;
    box-shadow: 0 0 0 3px ${props => props.$darkMode ? 'rgba(9, 105, 218, 0.3)' : 'rgba(9, 105, 218, 0.1)'};
  }
  
  &::placeholder {
    color: ${props => props.$darkMode ? '#6e7681' : '#6e7681'};
  }
`;

const NavItem = styled.div`
  padding: 10px 12px;
  margin: 2px 0;
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;
  color: ${props => props.$darkMode ? '#c9d1d9' : '#24292f'};
  transition: all 0.15s;
  
  &:hover {
    background: ${props => props.$darkMode ? '#161b22' : '#f6f8fa'};
    transform: translateX(2px);
  }
`;

const Method = styled.span`
  font-weight: 600;
  font-size: ${props => props.$small ? '10px' : '11px'};
  padding: ${props => props.$small ? '4px 8px' : '6px 10px'};
  border-radius: 6px;
  text-transform: uppercase;
  min-width: ${props => props.$small ? '50px' : '60px'};
  text-align: center;
  color: #fff;
  letter-spacing: 0.5px;
  background: ${props => {
    const colors = {get: '#1a7f37', post: '#0969da', put: '#bf8700', delete: '#cf222e', patch: '#8250df'};
    return colors[props.$method] || '#666';
  }};
  box-shadow: 0 1px 3px rgba(0,0,0,0.12);
`;

const Endpoint = styled.div`
  background: ${props => props.$darkMode ? '#161b22' : '#fff'};
  border: 1px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
  border-radius: 12px;
  padding: 32px;
  margin-bottom: 24px;
  color: ${props => props.$darkMode ? '#e6edf3' : '#24292f'};
  box-shadow: ${props => props.$darkMode ? '0 1px 3px rgba(0,0,0,0.3)' : '0 1px 3px rgba(0,0,0,0.06)'};
  transition: box-shadow 0.2s;
  
  &:hover {
    box-shadow: ${props => props.$darkMode ? '0 4px 12px rgba(0,0,0,0.4)' : '0 4px 12px rgba(0,0,0,0.08)'};
  }
`;

const Tabs = styled.div`
  display: flex;
  gap: 8px;
  border-bottom: 2px solid ${props => props.$darkMode ? '#21262d' : '#d0d7de'};
  margin: 24px 0 20px;
`;

const Tab = styled.button`
  background: ${props => props.$active ? (props.$darkMode ? '#21262d' : '#f6f8fa') : 'none'};
  border: none;
  color: ${props => props.$active ? (props.$darkMode ? '#58a6ff' : '#0969da') : (props.$darkMode ? '#8b949e' : '#57606a')};
  padding: 10px 20px;
  cursor: pointer;
  font-weight: 600;
  font-size: 14px;
  border-radius: 8px 8px 0 0;
  border-bottom: 3px solid ${props => props.$active ? (props.$darkMode ? '#58a6ff' : '#0969da') : 'transparent'};
  transition: all 0.2s;
  
  &:hover {
    color: ${props => props.$darkMode ? '#58a6ff' : '#0969da'};
    background: ${props => props.$darkMode ? '#21262d' : '#f6f8fa'};
  }
`;

const CodeBlock = styled.div`
  background: ${props => props.$darkMode ? '#0d1117' : '#f6f8fa'};
  border: 1px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
  border-radius: 8px;
  margin: 16px 0;
  overflow: hidden;
  box-shadow: ${props => props.$darkMode ? 'inset 0 1px 3px rgba(0,0,0,0.3)' : 'inset 0 1px 3px rgba(0,0,0,0.06)'};
`;

const CodeHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: ${props => props.$darkMode ? '#161b22' : '#fff'};
  border-bottom: 1px solid ${props => props.$darkMode ? '#30363d' : '#d0d7de'};
  color: ${props => props.$darkMode ? '#e6edf3' : '#24292f'};
  font-weight: 600;
  font-size: 13px;
`;

const CopyBtn = styled.button`
  background: ${props => props.$copied ? '#1a7f37' : '#0969da'};
  color: #fff;
  border: none;
  padding: 6px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  font-size: 12px;
  transition: all 0.2s;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(9, 105, 218, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

const Pre = styled.pre`
  padding: 16px;
  overflow-x: auto;
  font-family: 'SF Mono', Monaco, 'Cascadia Code', 'Roboto Mono', Consolas, 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.6;
  color: ${props => props.$darkMode ? '#c9d1d9' : '#24292f'};
  margin: 0;
`;

function CustomApiViewer({ spec, darkMode: initialDarkMode }) {
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState({});
  const [copied, setCopied] = useState({});
  const [showSdkGenerator, setShowSdkGenerator] = useState(false);
  const [sidebarWidth, setSidebarWidth] = useState(300);
  const [isResizing, setIsResizing] = useState(false);
  const [darkMode, setDarkMode] = useState(initialDarkMode);
  const [authToken, setAuthToken] = useState(() => localStorage.getItem('deviant-auth-token') || '');
  const [apiKey, setApiKey] = useState(() => localStorage.getItem('deviant-api-key') || '');
  const [corsEnabled, setCorsEnabled] = useState(true);
  const [responses, setResponses] = useState({});
  const [loading, setLoading] = useState({});
  const [history, setHistory] = useState(() => JSON.parse(localStorage.getItem('deviant-history') || '[]'));
  const [baseUrl, setBaseUrl] = useState(() => localStorage.getItem('deviant-base-url') || spec.servers?.[0]?.url || '');
  const [showHistory, setShowHistory] = useState(false);
  const [customUrl, setCustomUrl] = useState('');
  const [useCustomUrl, setUseCustomUrl] = useState(() => {
    const saved = localStorage.getItem('deviant-base-url');
    return saved && !spec.servers?.some(s => s.url === saved);
  });
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const [paramValues, setParamValues] = useState({});
  const [bodyValues, setBodyValues] = useState({});
  const [collections, setCollections] = useState(() => JSON.parse(localStorage.getItem('deviant-collections') || '[]'));
  const [showCollections, setShowCollections] = useState(false);
  const [variables, setVariables] = useState(() => JSON.parse(localStorage.getItem('deviant-variables') || '{}'));
  const [showVariables, setShowVariables] = useState(false);
  const [responseComparison, setResponseComparison] = useState({});
  const [showComparison, setShowComparison] = useState(false);
  const [testScripts, setTestScripts] = useState(() => JSON.parse(localStorage.getItem('deviant-test-scripts') || '{}'));
  const [testResults, setTestResults] = useState({});
  const [modal, setModal] = useState(null);
  const [autocomplete, setAutocomplete] = useState({ show: false, items: [], position: { top: 0, left: 0 }, selected: 0, inputId: null });
  const [fileValues, setFileValues] = useState({});
  
  const commonHeaders = [
    'Accept', 'Accept-Encoding', 'Accept-Language', 'Authorization', 'Cache-Control',
    'Content-Type', 'Cookie', 'Host', 'Origin', 'Referer', 'User-Agent',
    'X-API-Key', 'X-Request-ID', 'X-Forwarded-For', 'X-Custom-Header'
  ];

  React.useEffect(() => {
    localStorage.setItem('deviant-auth-token', authToken);
  }, [authToken]);

  React.useEffect(() => {
    localStorage.setItem('deviant-api-key', apiKey);
  }, [apiKey]);

  React.useEffect(() => {
    localStorage.setItem('deviant-base-url', baseUrl);
  }, [baseUrl]);

  React.useEffect(() => {
    if (useCustomUrl && customUrl) {
      setBaseUrl(customUrl);
    }
  }, [useCustomUrl, customUrl]);

  React.useEffect(() => {
    localStorage.setItem('deviant-history', JSON.stringify(history.slice(0, 50)));
  }, [history]);

  React.useEffect(() => {
    localStorage.setItem('deviant-collections', JSON.stringify(collections));
  }, [collections]);

  React.useEffect(() => {
    localStorage.setItem('deviant-variables', JSON.stringify(variables));
  }, [variables]);

  React.useEffect(() => {
    localStorage.setItem('deviant-test-scripts', JSON.stringify(testScripts));
  }, [testScripts]);

  React.useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.ctrlKey && e.key === 'Enter') {
        const sendBtn = document.querySelector('[data-send-request]');
        if (sendBtn) sendBtn.click();
      }
      if (autocomplete.show) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setAutocomplete(prev => ({ ...prev, selected: Math.min(prev.selected + 1, prev.items.length - 1) }));
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          setAutocomplete(prev => ({ ...prev, selected: Math.max(prev.selected - 1, 0) }));
        } else if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          const item = autocomplete.items[autocomplete.selected];
          if (item && autocomplete.inputId) {
            const input = document.getElementById(autocomplete.inputId);
            if (input) {
              const cursorPos = input.selectionStart;
              const text = input.value;
              const beforeCursor = text.substring(0, cursorPos);
              const afterCursor = text.substring(cursorPos);
              const lastWord = beforeCursor.match(/{{(\w*)$/)?.[1] || beforeCursor.match(/(\S*)$/)?.[0] || '';
              const newValue = beforeCursor.substring(0, beforeCursor.length - lastWord.length - (beforeCursor.endsWith('{{') ? 2 : 0)) + item + afterCursor;
              input.value = newValue;
              input.dispatchEvent(new Event('input', { bubbles: true }));
            }
          }
          setAutocomplete({ show: false, items: [], position: { top: 0, left: 0 }, selected: 0, inputId: null });
        } else if (e.key === 'Escape') {
          setAutocomplete({ show: false, items: [], position: { top: 0, left: 0 }, selected: 0, inputId: null });
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [autocomplete]);

  const handleInputChange = (e, type) => {
    const input = e.target;
    const value = input.value;
    const cursorPos = input.selectionStart;
    const textBeforeCursor = value.substring(0, cursorPos);
    
    if (textBeforeCursor.includes('{{')) {
      const match = textBeforeCursor.match(/{{(\w*)$/);
      if (match) {
        const query = match[1].toLowerCase();
        const filtered = Object.keys(variables).filter(v => v.toLowerCase().includes(query));
        if (filtered.length > 0) {
          const rect = input.getBoundingClientRect();
          setAutocomplete({
            show: true,
            items: filtered.map(v => `{{${v}}}`),
            position: { top: rect.bottom + window.scrollY, left: rect.left + window.scrollX },
            selected: 0,
            inputId: input.id
          });
          return;
        }
      }
    }
    
    if (type === 'header') {
      const lastWord = textBeforeCursor.split(/\s+/).pop();
      const filtered = commonHeaders.filter(h => h.toLowerCase().startsWith(lastWord.toLowerCase()));
      if (filtered.length > 0 && lastWord.length > 0) {
        const rect = input.getBoundingClientRect();
        setAutocomplete({
          show: true,
          items: filtered,
          position: { top: rect.bottom + window.scrollY, left: rect.left + window.scrollX },
          selected: 0,
          inputId: input.id
        });
        return;
      }
    }
    
    setAutocomplete({ show: false, items: [], position: { top: 0, left: 0 }, selected: 0, inputId: null });
  };

  React.useEffect(() => {
    const handleClickOutside = (e) => {
      if (showDownloadMenu && !e.target.closest('[data-download-menu]')) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [showDownloadMenu]);

  const handleMouseDown = () => setIsResizing(true);

  const handleMouseMove = (e) => {
    if (!isResizing) return;
    const newWidth = e.clientX;
    if (newWidth >= 200 && newWidth <= 600) {
      setSidebarWidth(newWidth);
    }
  };

  const handleMouseUp = () => setIsResizing(false);

  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isResizing]);

  const copy = (id, text) => {
    navigator.clipboard.writeText(text);
    setCopied({...copied, [id]: true});
    setTimeout(() => setCopied({...copied, [id]: false}), 2000);
  };

  const genSample = (schema, visited = new Set()) => {
    if (!schema) return {};
    if (schema.$ref) {
      const ref = schema.$ref.replace('#/components/schemas/', '');
      if (visited.has(ref)) return {};
      visited.add(ref);
      const resolved = spec.components?.schemas?.[ref];
      return resolved ? genSample(resolved, visited) : {};
    }
    if (schema.example) return schema.example;
    if (schema.type === 'object' && schema.properties) {
      const obj = {};
      for (const k in schema.properties) {
        obj[k] = genSample(schema.properties[k], visited);
      }
      return obj;
    }
    if (schema.type === 'array') return schema.items ? [genSample(schema.items, visited)] : [];
    if (schema.type === 'string') return schema.enum?.[0] || 'string';
    if (schema.type === 'integer') return 0;
    if (schema.type === 'number') return 0.0;
    if (schema.type === 'boolean') return true;
    return null;
  };

  const resolveSchema = (schema) => {
    if (!schema) return null;
    if (schema.$ref) {
      const ref = schema.$ref.replace('#/components/schemas/', '');
      return spec.components?.schemas?.[ref] || schema;
    }
    return schema;
  };

  const endpoints = [];
  for (const path in spec.paths || {}) {
    for (const method in spec.paths[path]) {
      const endpoint = spec.paths[path][method];
      const id = `${method}-${path}`;
      if (search && !`${method} ${path} ${endpoint.summary || ''}`.toLowerCase().includes(search.toLowerCase())) continue;
      endpoints.push({ path, method, endpoint, id });
    }
  }

  const schemas = Object.keys(spec.components?.schemas || {});

  return (
    <Layout $darkMode={darkMode}>
      <Sidebar $darkMode={darkMode} $width={sidebarWidth}>
        <ResizeHandle onMouseDown={handleMouseDown} />
        <Search $darkMode={darkMode} placeholder="Search..." value={search} onChange={e => setSearch(e.target.value)} />
        <div style={{ fontSize: '11px', fontWeight: '700', color: darkMode ? '#8b949e' : '#57606a', textTransform: 'uppercase', marginBottom: '12px', letterSpacing: '1px', paddingLeft: '4px' }}>Endpoints</div>
        {endpoints.map(({ path, method, id }) => (
          <NavItem key={id} $darkMode={darkMode} onClick={() => document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })}>
            <Method $method={method} $small>{method}</Method>
            <span style={{ fontFamily: 'SF Mono, Monaco, monospace', fontSize: '12px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{path}</span>
          </NavItem>
        ))}
        {schemas.length > 0 && (
          <>
            <div style={{ fontSize: '11px', fontWeight: '700', color: darkMode ? '#8b949e' : '#57606a', textTransform: 'uppercase', marginTop: '32px', marginBottom: '12px', letterSpacing: '1px', paddingLeft: '4px' }}>Schemas</div>
            {schemas.map(schemaName => (
              <NavItem key={schemaName} $darkMode={darkMode} onClick={() => document.getElementById('schema-' + schemaName)?.scrollIntoView({ behavior: 'smooth' })}>
                <span style={{ 
                  fontSize: '10px', 
                  fontWeight: '700', 
                  padding: '4px 8px', 
                  borderRadius: '4px', 
                  background: darkMode ? '#30363d' : '#e1e4e8',
                  color: darkMode ? '#8b949e' : '#57606a'
                }}>SCHEMA</span>
                <span style={{ fontSize: '12px', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{schemaName}</span>
              </NavItem>
            ))}
          </>
        )}
      </Sidebar>
      <Main>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
          <div>
            <h1 style={{ fontSize: '2.5em', marginBottom: '8px', fontWeight: '700', color: darkMode ? '#58a6ff' : '#0969da', letterSpacing: '-0.5px' }}>{spec.info?.title || 'API Documentation'}</h1>
            <p style={{ fontSize: '1.1em', color: darkMode ? '#8b949e' : '#6e7681', lineHeight: '1.6' }}>{spec.info?.description || ''}</p>
          </div>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            <div style={{ position: 'relative' }} data-download-menu>
              <button
                onClick={() => setShowDownloadMenu(!showDownloadMenu)}
                style={{
                  padding: '10px 16px',
                  background: darkMode ? '#30363d' : '#f6f8fa',
                  color: darkMode ? '#e6edf3' : '#24292f',
                  border: `1px solid ${darkMode ? '#484f58' : '#d0d7de'}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                Download ▾
              </button>
              {showDownloadMenu && (
                <div style={{
                  position: 'absolute',
                  top: '48px',
                  right: 0,
                  background: darkMode ? '#161b22' : '#fff',
                  border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`,
                  borderRadius: '8px',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
                  zIndex: 1000,
                  minWidth: '140px'
                }}>
                  <button
                    onClick={() => {
                      const blob = new Blob([yaml.dump(spec)], { type: 'text/yaml' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${spec.info?.title || 'openapi'}.yaml`;
                      a.click();
                      URL.revokeObjectURL(url);
                      setShowDownloadMenu(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      background: 'transparent',
                      color: darkMode ? '#e6edf3' : '#24292f',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '14px',
                      fontWeight: '600',
                      borderRadius: '8px 8px 0 0'
                    }}
                    onMouseEnter={(e) => e.target.style.background = darkMode ? '#21262d' : '#f6f8fa'}
                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                  >
                    YAML
                  </button>
                  <button
                    onClick={() => {
                      const blob = new Blob([JSON.stringify(spec, null, 2)], { type: 'application/json' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `${spec.info?.title || 'openapi'}.json`;
                      a.click();
                      URL.revokeObjectURL(url);
                      setShowDownloadMenu(false);
                    }}
                    style={{
                      width: '100%',
                      padding: '10px 16px',
                      background: 'transparent',
                      color: darkMode ? '#e6edf3' : '#24292f',
                      border: 'none',
                      cursor: 'pointer',
                      textAlign: 'left',
                      fontSize: '14px',
                      fontWeight: '600',
                      borderRadius: '0 0 8px 8px'
                    }}
                    onMouseEnter={(e) => e.target.style.background = darkMode ? '#21262d' : '#f6f8fa'}
                    onMouseLeave={(e) => e.target.style.background = 'transparent'}
                  >
                    JSON
                  </button>
                </div>
              )}
            </div>
            <button
              onClick={() => setDarkMode(!darkMode)}
              style={{
                padding: '10px',
                background: darkMode ? '#30363d' : '#f6f8fa',
                color: darkMode ? '#e6edf3' : '#24292f',
                border: `1px solid ${darkMode ? '#484f58' : '#d0d7de'}`,
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '44px',
                height: '44px'
              }}
              title={darkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
            >
              {darkMode ? '☀️' : '🌙'}
            </button>
            <button
              onClick={() => setShowSdkGenerator(true)}
              style={{
                padding: '12px 20px',
                background: '#0969da',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px',
                whiteSpace: 'nowrap',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              📦 Generate SDK
            </button>
          </div>
        </div>
        {showSdkGenerator && <SdkGeneratorModal spec={spec} darkMode={darkMode} onClose={() => setShowSdkGenerator(false)} />}
        {endpoints.map(({ path, method, endpoint, id }) => {
          const tab = activeTab[id] || 'overview';
          const body = endpoint.requestBody?.content?.['application/json']?.schema;
          const samp = body ? JSON.stringify(genSample(body), null, 2) : null;
          
          const buildUrl = () => {
            let finalPath = path;
            const queryParams = [];
            
            // Extract path params from the path pattern
            const pathParamNames = (path.match(/\{([^}]+)\}/g) || []).map(p => p.slice(1, -1));
            
            // Replace path parameters
            pathParamNames.forEach(paramName => {
              const value = paramValues[`${id}-${paramName}`] || '';
              if (value) {
                finalPath = finalPath.replace(`{${paramName}}`, encodeURIComponent(replaceVars(value)));
              }
            });
            
            // Handle query parameters from spec
            if (endpoint.parameters) {
              endpoint.parameters.forEach(param => {
                if (param.in === 'query') {
                  const value = paramValues[`${id}-${param.name}`] || '';
                  if (value) {
                    queryParams.push(`${encodeURIComponent(param.name)}=${encodeURIComponent(replaceVars(value))}`);
                  }
                }
              });
            }
            
            const queryString = queryParams.length > 0 ? '?' + queryParams.join('&') : '';
            return (baseUrl || spec.servers?.[0]?.url || 'https://api.example.com') + finalPath + queryString;
          };
          
          const replaceVars = (str) => {
            if (!str) return str;
            return str.replace(/\{\{(\w+)\}\}/g, (match, key) => variables[key] || match);
          };
          
          let url = buildUrl();
          const curl = `curl -X ${method.toUpperCase()} "${url}" -H "Content-Type: application/json"${samp ? ` -d '${samp}'` : ''}`;
          const js = `fetch("${url}", {\n  method: "${method.toUpperCase()}",\n  headers: {"Content-Type": "application/json"}${samp ? `,\n  body: JSON.stringify(${samp})` : ''}\n})\n.then(r => r.json())\n.then(d => console.log(d));`;
          const py = `import requests\n\nr = requests.${method}("${url}"${samp ? `,\n    json=${samp}` : ''})\nprint(r.json())`;
          const go = `package main\n\nimport (\n    "bytes"\n    "encoding/json"\n    "net/http"\n)\n\nfunc main() {\n    ${samp ? `data := []byte(\`${samp}\`)\n    ` : ''}resp, _ := http.${method === 'get' ? 'Get' : 'Post'}("${url}"${samp ? ', "application/json", bytes.NewBuffer(data)' : ''})\n    defer resp.Body.Close()\n}`;
          const ruby = `require 'net/http'\nrequire 'json'\n\nuri = URI('${url}')\n${samp ? `data = ${samp}\n` : ''}req = Net::HTTP::${method.charAt(0).toUpperCase() + method.slice(1).toLowerCase()}.new(uri)\nreq['Content-Type'] = 'application/json'\n${samp ? `req.body = data.to_json\n` : ''}res = Net::HTTP.start(uri.hostname, uri.port) { |http| http.request(req) }\nputs res.body`;
          const php = `<?php\n$ch = curl_init('${url}');\ncurl_setopt($ch, CURLOPT_CUSTOMREQUEST, '${method.toUpperCase()}');\n${samp ? `curl_setopt($ch, CURLOPT_POSTFIELDS, '${samp}');\n` : ''}curl_setopt($ch, CURLOPT_HTTPHEADER, ['Content-Type: application/json']);\ncurl_setopt($ch, CURLOPT_RETURNTRANSFER, true);\n$response = curl_exec($ch);\ncurl_close($ch);\necho $response;\n?>`;
          const sampClean = samp ? samp.replace(/\n/g, '') : '';
          const java = `import java.net.http.*;\nimport java.net.URI;\n\nHttpClient client = HttpClient.newHttpClient();\nHttpRequest request = HttpRequest.newBuilder()\n    .uri(URI.create("${url}"))\n    .header("Content-Type", "application/json")\n    ${method === 'get' ? '.GET()' : `.method("${method.toUpperCase()}", ${samp ? `HttpRequest.BodyPublishers.ofString("${sampClean}")` : 'HttpRequest.BodyPublishers.noBody()'})`}\n    .build();\nHttpResponse<String> response = client.send(request, HttpResponse.BodyHandlers.ofString());\nSystem.out.println(response.body());`;

          return (
            <Endpoint key={id} id={id} $darkMode={darkMode}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <Method $method={method}>{method}</Method>
                <span style={{ fontFamily: 'SF Mono, Monaco, monospace', fontSize: '17px', fontWeight: '600', color: darkMode ? '#e6edf3' : '#24292f' }}>{path}</span>
              </div>
              {endpoint.summary && <p style={{ fontSize: '16px', marginBottom: '8px', fontWeight: '500', lineHeight: '1.5' }}>{endpoint.summary}</p>}
              {endpoint.description && <p style={{ color: darkMode ? '#8b949e' : '#6e7681', marginBottom: '20px', lineHeight: '1.6', fontSize: '14px' }}>{endpoint.description}</p>}
              
              <Tabs $darkMode={darkMode}>
                <Tab $active={tab === 'overview'} $darkMode={darkMode} onClick={() => setActiveTab({...activeTab, [id]: 'overview'})}>Overview</Tab>
                <Tab $active={tab === 'try'} $darkMode={darkMode} onClick={() => setActiveTab({...activeTab, [id]: 'try'})}>Try It</Tab>
                <Tab $active={tab === 'code'} $darkMode={darkMode} onClick={() => setActiveTab({...activeTab, [id]: 'code'})}>Code</Tab>
              </Tabs>

              {tab === 'overview' && (
                <>
                  {endpoint.parameters && endpoint.parameters.length > 0 && (
                    <>
                      <h3 style={{ fontSize: '17px', fontWeight: 700, margin: '24px 0 16px', color: darkMode ? '#58a6ff' : '#0969da', letterSpacing: '-0.3px' }}>Parameters</h3>
                      {endpoint.parameters.map((param, i) => (
                        <div key={i} style={{ padding: '14px 16px', background: darkMode ? '#0d1117' : '#f6f8fa', borderRadius: '8px', marginBottom: '10px', border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}` }}>
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '6px' }}>
                            <code style={{ fontWeight: 600, fontSize: '14px', color: darkMode ? '#58a6ff' : '#0969da' }}>{param.name}</code>
                            <span style={{ fontSize: '11px', padding: '3px 8px', background: darkMode ? '#30363d' : '#e1e4e8', borderRadius: '6px', fontWeight: '600' }}>{param.in}</span>
                            {param.required && <span style={{ fontSize: '11px', padding: '3px 8px', background: '#cf222e', color: '#fff', borderRadius: '6px', fontWeight: '600' }}>required</span>}
                          </div>
                          <div style={{ fontSize: '13px', color: darkMode ? '#8b949e' : '#6e7681', lineHeight: '1.5' }}>{param.description || 'No description'}</div>
                          {param.schema && <div style={{ fontSize: '12px', color: darkMode ? '#8b949e' : '#6e7681', marginTop: '6px', fontFamily: 'monospace' }}>Type: {param.schema.type}</div>}
                        </div>
                      ))}
                    </>
                  )}
                  {endpoint.security && endpoint.security.length > 0 && (
                    <>
                      <h3 style={{ fontSize: '17px', fontWeight: 700, margin: '24px 0 16px', color: darkMode ? '#58a6ff' : '#0969da', letterSpacing: '-0.3px' }}>Security</h3>
                      {endpoint.security.map((sec, i) => (
                        <div key={i} style={{ padding: '14px 16px', background: darkMode ? '#0d1117' : '#f6f8fa', borderRadius: '8px', marginBottom: '10px', border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}` }}>
                          {Object.keys(sec).map(k => (
                            <div key={k}>
                              <code style={{ fontWeight: 700, color: darkMode ? '#58a6ff' : '#0969da' }}>{k}</code>
                              {sec[k].length > 0 && <span style={{ marginLeft: '8px', fontSize: '13px', color: darkMode ? '#8b949e' : '#57606a' }}>Scopes: {sec[k].join(', ')}</span>}
                            </div>
                          ))}
                        </div>
                      ))}
                    </>
                  )}
                  {body && (
                    <>
                      <h3 style={{ fontSize: '17px', fontWeight: 700, margin: '24px 0 16px', color: darkMode ? '#58a6ff' : '#0969da', letterSpacing: '-0.3px' }}>Request Body</h3>
                      <CodeBlock $darkMode={darkMode}>
                        <Pre $darkMode={darkMode}>{JSON.stringify(resolveSchema(body), null, 2)}</Pre>
                      </CodeBlock>
                    </>
                  )}
                  <h3 style={{ fontSize: '17px', fontWeight: 700, margin: '24px 0 16px', color: darkMode ? '#58a6ff' : '#0969da', letterSpacing: '-0.3px' }}>Responses</h3>
                  {Object.entries(endpoint.responses || {}).map(([code, resp]) => (
                    <div key={code} style={{ margin: '16px 0', padding: '12px', background: darkMode ? '#0d1117' : '#f6f8fa', borderRadius: '8px', border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}` }}>
                      <div style={{ marginBottom: '8px' }}>
                        <strong style={{ color: darkMode ? '#58a6ff' : '#0969da', fontSize: '15px', fontWeight: '600' }}>{code}</strong>
                        <span style={{ color: darkMode ? '#8b949e' : '#6e7681', marginLeft: '8px', fontSize: '14px' }}>{resp.description || ''}</span>
                      </div>
                      {resp.content?.['application/json']?.schema && (
                        <CodeBlock $darkMode={darkMode}>
                          <Pre $darkMode={darkMode}>{JSON.stringify(resolveSchema(resp.content['application/json'].schema), null, 2)}</Pre>
                        </CodeBlock>
                      )}
                    </div>
                  ))}
                </>
              )}

              {tab === 'try' && (
                <>
                  {(() => {
                    const pathParamNames = (path.match(/\{([^}]+)\}/g) || []).map(p => p.slice(1, -1));
                    const specParams = (endpoint.parameters || []).filter(p => p.name);
                    
                    const pathParams = pathParamNames.map(name => 
                      specParams.find(p => p.name === name && p.in === 'path') || 
                      { name, in: 'path', required: true }
                    );
                    
                    const otherParams = specParams.filter(p => p.in !== 'path' && !pathParamNames.includes(p.name));
                    const allParams = [...pathParams, ...otherParams];
                    
                    return allParams.length > 0 ? (
                      <div style={{ marginBottom: '24px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '16px', color: darkMode ? '#58a6ff' : '#0969da', letterSpacing: '-0.3px' }}>Parameters</h3>
                        {allParams.map((param, i) => (
                          <div key={i} style={{ marginBottom: '16px' }}>
                            <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: darkMode ? '#e6edf3' : '#24292f' }}>
                              {param.name}
                              {param.required && <span style={{ color: '#cf222e', marginLeft: '4px' }}>*</span>}
                              <span style={{ marginLeft: '8px', fontSize: '11px', padding: '3px 8px', background: darkMode ? '#30363d' : '#e1e4e8', borderRadius: '6px', fontWeight: '500' }}>{param.in}</span>
                            </label>
                            <input
                              type="text"
                              id={`param-${id}-${param.name}`}
                              placeholder={(typeof param.description === 'string' ? param.description : '') || `Enter ${param.name}`}
                              value={paramValues[`${id}-${param.name}`] || ''}
                              onChange={(e) => {
                                setParamValues({...paramValues, [`${id}-${param.name}`]: e.target.value});
                                handleInputChange(e, 'param');
                              }}
                              style={{
                                width: '100%',
                                padding: '10px 12px',
                                background: darkMode ? '#0d1117' : '#fff',
                                border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`,
                                borderRadius: '8px',
                                color: darkMode ? '#e6edf3' : '#24292f',
                                fontSize: '14px',
                                transition: 'all 0.2s',
                                boxSizing: 'border-box'
                              }}
                              onFocus={(e) => {
                                e.target.style.borderColor = '#0969da';
                                e.target.style.boxShadow = darkMode ? '0 0 0 3px rgba(9, 105, 218, 0.3)' : '0 0 0 3px rgba(9, 105, 218, 0.1)';
                              }}
                              onBlur={(e) => {
                                e.target.style.borderColor = darkMode ? '#30363d' : '#d0d7de';
                                e.target.style.boxShadow = 'none';
                              }}
                            />
                          </div>
                        ))}
                      </div>
                    ) : null;
                  })()}
                  {endpoint.requestBody?.content?.['multipart/form-data'] ? (
                    <div style={{ marginBottom: '24px' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '16px', color: darkMode ? '#58a6ff' : '#0969da', letterSpacing: '-0.3px' }}>Form Data</h3>
                      {Object.entries(endpoint.requestBody.content['multipart/form-data'].schema?.properties || {}).map(([fieldName, fieldSchema]) => (
                        <div key={fieldName} style={{ marginBottom: '16px' }}>
                          <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '600', color: darkMode ? '#e6edf3' : '#24292f' }}>
                            {fieldName}
                            {endpoint.requestBody.content['multipart/form-data'].schema?.required?.includes(fieldName) && <span style={{ color: '#cf222e', marginLeft: '4px' }}>*</span>}
                            <span style={{ marginLeft: '8px', fontSize: '11px', color: darkMode ? '#8b949e' : '#57606a' }}>{fieldSchema.type === 'string' && fieldSchema.format === 'binary' ? 'file' : fieldSchema.type}</span>
                          </label>
                          {fieldSchema.type === 'string' && fieldSchema.format === 'binary' ? (
                            <input
                              type="file"
                              onChange={(e) => setFileValues({...fileValues, [`${id}-${fieldName}`]: e.target.files[0]})}
                              style={{
                                width: '100%',
                                padding: '10px',
                                background: darkMode ? '#0d1117' : '#fff',
                                border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`,
                                borderRadius: '6px',
                                color: darkMode ? '#e6edf3' : '#24292f',
                                fontSize: '14px'
                              }}
                            />
                          ) : (
                            <input
                              type="text"
                              placeholder={fieldSchema.description || `Enter ${fieldName}`}
                              value={paramValues[`${id}-${fieldName}`] || ''}
                              onChange={(e) => setParamValues({...paramValues, [`${id}-${fieldName}`]: e.target.value})}
                              style={{
                                width: '100%',
                                padding: '10px',
                                background: darkMode ? '#0d1117' : '#fff',
                                border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`,
                                borderRadius: '6px',
                                color: darkMode ? '#e6edf3' : '#24292f',
                                fontSize: '14px'
                              }}
                            />
                          )}
                        </div>
                      ))}
                    </div>
                  ) : body ? (
                    <div style={{ marginBottom: '24px' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '16px', color: darkMode ? '#58a6ff' : '#0969da', letterSpacing: '-0.3px' }}>Request Body</h3>
                      <textarea
                        id={`body-${id}`}
                        value={bodyValues[id] !== undefined ? bodyValues[id] : samp}
                        onChange={(e) => {
                          setBodyValues({...bodyValues, [id]: e.target.value});
                          handleInputChange(e, 'body');
                        }}
                        style={{
                          width: '100%',
                          minHeight: '200px',
                          padding: '14px',
                          background: darkMode ? '#0d1117' : '#fff',
                          border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`,
                          borderRadius: '8px',
                          color: darkMode ? '#e6edf3' : '#24292f',
                          fontFamily: 'SF Mono, Monaco, monospace',
                          fontSize: '13px',
                          lineHeight: '1.6',
                          resize: 'vertical',
                          transition: 'all 0.2s',
                          boxSizing: 'border-box'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#0969da';
                          e.target.style.boxShadow = darkMode ? '0 0 0 3px rgba(9, 105, 218, 0.3)' : '0 0 0 3px rgba(9, 105, 218, 0.1)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = darkMode ? '#30363d' : '#d0d7de';
                          e.target.style.boxShadow = 'none';
                        }}
                      />
                    </div>
                  ) : null}
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '16px', color: darkMode ? '#58a6ff' : '#0969da', letterSpacing: '-0.3px' }}>Test Script</h3>
                    <textarea
                      placeholder="// Write test assertions\n// Example:\nif (status !== 200) throw new Error('Expected 200');\nif (!data.id) throw new Error('Missing id field');"
                      value={testScripts[id] || ''}
                      onChange={(e) => setTestScripts({...testScripts, [id]: e.target.value})}
                      style={{
                        width: '100%',
                        minHeight: '120px',
                        padding: '12px',
                        background: darkMode ? '#0d1117' : '#fff',
                        border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`,
                        borderRadius: '6px',
                        color: darkMode ? '#e6edf3' : '#24292f',
                        fontFamily: 'monospace',
                        fontSize: '13px',
                        resize: 'vertical'
                      }}
                    />
                    {testResults[id] && (
                      <div style={{ marginTop: '8px', padding: '8px', background: testResults[id].passed ? (darkMode ? '#0d1f17' : '#dafbe1') : (darkMode ? '#1a1f2e' : '#fff5f5'), border: `1px solid ${testResults[id].passed ? '#1a7f37' : '#cf222e'}`, borderRadius: '4px', color: testResults[id].passed ? '#1a7f37' : '#cf222e', fontSize: '12px' }}>
                        {testResults[id].passed ? '✓' : '✗'} {testResults[id].message}
                      </div>
                    )}
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '16px', color: darkMode ? '#58a6ff' : '#0969da', letterSpacing: '-0.3px' }}>Server</h3>
                    {spec.servers && spec.servers.length > 0 && (
                      <select
                        value={useCustomUrl ? 'custom' : baseUrl}
                        onChange={(e) => {
                          if (e.target.value === 'custom') {
                            setUseCustomUrl(true);
                          } else {
                            setUseCustomUrl(false);
                            setBaseUrl(e.target.value);
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '10px',
                          background: darkMode ? '#0d1117' : '#fff',
                          border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`,
                          borderRadius: '6px',
                          color: darkMode ? '#e6edf3' : '#24292f',
                          marginBottom: useCustomUrl ? '12px' : '0',
                          cursor: 'pointer'
                        }}
                      >
                        {spec.servers.map((server, i) => (
                          <option key={i} value={server.url}>
                            {server.url}{server.description ? ` - ${server.description}` : ''}
                          </option>
                        ))}
                        <option value="custom">Custom URL</option>
                      </select>
                    )}
                    {useCustomUrl && (
                      <input
                        type="text"
                        placeholder="Enter custom base URL"
                        value={customUrl}
                        onChange={(e) => setCustomUrl(e.target.value)}
                        style={{
                          width: '100%',
                          padding: '10px',
                          background: darkMode ? '#0d1117' : '#fff',
                          border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`,
                          borderRadius: '6px',
                          color: darkMode ? '#e6edf3' : '#24292f'
                        }}
                      />
                    )}
                  </div>
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: darkMode ? '#58a6ff' : '#0969da', letterSpacing: '-0.3px' }}>Authentication</h3>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => setShowVariables(!showVariables)}
                          style={{
                            padding: '6px 12px',
                            background: darkMode ? '#21262d' : '#f6f8fa',
                            border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`,
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: darkMode ? '#e6edf3' : '#24292f'
                          }}
                        >
                          Variables
                        </button>
                        <button
                          onClick={() => {
                            setModal({
                              type: 'prompt',
                              title: 'Save to Collection',
                              message: 'Enter collection name:',
                              onConfirm: (name) => {
                                if (name) {
                                  setCollections([...collections, {
                                    id: Date.now(),
                                    name,
                                    endpoint: id,
                                    params: paramValues,
                                    body: bodyValues[id],
                                    auth: { token: authToken, apiKey }
                                  }]);
                                }
                                setModal(null);
                              },
                              onCancel: () => setModal(null)
                            });
                          }}
                          style={{
                            padding: '6px 12px',
                            background: darkMode ? '#21262d' : '#f6f8fa',
                            border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`,
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: darkMode ? '#e6edf3' : '#24292f'
                          }}
                        >
                          Save to Collection
                        </button>
                        <button
                          onClick={() => setShowCollections(!showCollections)}
                          style={{
                            padding: '6px 12px',
                            background: darkMode ? '#21262d' : '#f6f8fa',
                            border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`,
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: darkMode ? '#e6edf3' : '#24292f'
                          }}
                        >
                          Collections ({collections.length})
                        </button>
                        <button
                          onClick={() => {
                            if (responses[id]) {
                              setResponseComparison({...responseComparison, [id]: responses[id]});
                              setShowComparison(true);
                            }
                          }}
                          style={{
                            padding: '6px 12px',
                            background: darkMode ? '#21262d' : '#f6f8fa',
                            border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`,
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: darkMode ? '#e6edf3' : '#24292f'
                          }}
                        >
                          Compare
                        </button>
                        <button
                          onClick={() => setShowHistory(!showHistory)}
                          style={{
                            padding: '6px 12px',
                            background: darkMode ? '#21262d' : '#f6f8fa',
                            border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`,
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '12px',
                            fontWeight: '600',
                            color: darkMode ? '#e6edf3' : '#24292f'
                          }}
                        >
                          History ({history.length})
                        </button>
                      </div>
                    </div>
                    {showVariables && (
                      <div style={{ marginBottom: '12px', background: darkMode ? '#0d1117' : '#f6f8fa', border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`, borderRadius: '6px', padding: '12px' }}>
                        <div style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                          <input
                            placeholder="Variable name"
                            id="var-name"
                            style={{
                              flex: 1,
                              padding: '8px',
                              background: darkMode ? '#161b22' : '#fff',
                              border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`,
                              borderRadius: '4px',
                              color: darkMode ? '#e6edf3' : '#24292f',
                              fontSize: '12px'
                            }}
                          />
                          <input
                            placeholder="Value"
                            id="var-value"
                            style={{
                              flex: 1,
                              padding: '8px',
                              background: darkMode ? '#161b22' : '#fff',
                              border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`,
                              borderRadius: '4px',
                              color: darkMode ? '#e6edf3' : '#24292f',
                              fontSize: '12px'
                            }}
                          />
                          <button
                            onClick={() => {
                              const name = document.getElementById('var-name').value;
                              const value = document.getElementById('var-value').value;
                              if (name && value) {
                                setVariables({...variables, [name]: value});
                                document.getElementById('var-name').value = '';
                                document.getElementById('var-value').value = '';
                              }
                            }}
                            style={{
                              padding: '8px 16px',
                              background: '#0969da',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '600'
                            }}
                          >
                            Add
                          </button>
                        </div>
                        {Object.entries(variables).map(([k, v]) => (
                          <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px', background: darkMode ? '#161b22' : '#fff', borderRadius: '4px', marginBottom: '4px', fontSize: '12px' }}>
                            <span><code style={{ color: darkMode ? '#58a6ff' : '#0969da' }}>{`{{${k}}}`}</code> = {v}</span>
                            <button
                              onClick={() => {
                                const newVars = {...variables};
                                delete newVars[k];
                                setVariables(newVars);
                              }}
                              style={{
                                padding: '4px 8px',
                                background: '#cf222e',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '11px'
                              }}
                            >
                              Delete
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                    {showCollections && collections.length > 0 && (
                      <div style={{ marginBottom: '12px', maxHeight: '200px', overflowY: 'auto', background: darkMode ? '#0d1117' : '#f6f8fa', border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`, borderRadius: '6px', padding: '8px' }}>
                        {collections.map(c => (
                          <div key={c.id} style={{ padding: '8px', marginBottom: '4px', background: darkMode ? '#161b22' : '#fff', borderRadius: '4px', fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontWeight: '600' }}>{c.name}</span>
                            <div style={{ display: 'flex', gap: '4px' }}>
                              <button
                                onClick={() => {
                                  setParamValues(c.params || {});
                                  setBodyValues({...bodyValues, [c.endpoint]: c.body});
                                  setAuthToken(c.auth?.token || '');
                                  setApiKey(c.auth?.apiKey || '');
                                }}
                                style={{
                                  padding: '4px 8px',
                                  background: '#0969da',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '11px'
                                }}
                              >
                                Load
                              </button>
                              <button
                                onClick={() => setCollections(collections.filter(col => col.id !== c.id))}
                                style={{
                                  padding: '4px 8px',
                                  background: '#cf222e',
                                  color: '#fff',
                                  border: 'none',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '11px'
                                }}
                              >
                                Delete
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    {showHistory && history.length > 0 && (
                      <div style={{ marginBottom: '12px', maxHeight: '200px', overflowY: 'auto', background: darkMode ? '#0d1117' : '#f6f8fa', border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`, borderRadius: '6px', padding: '8px' }}>
                        {history.map(h => (
                          <div key={h.id} style={{ padding: '8px', marginBottom: '4px', background: darkMode ? '#161b22' : '#fff', borderRadius: '4px', fontSize: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <div>
                              <Method $method={h.method.toLowerCase()} $small>{h.method}</Method>
                              <span style={{ marginLeft: '8px', fontFamily: 'monospace' }}>{h.url}</span>
                            </div>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                              <span style={{ color: h.status < 300 ? '#1a7f37' : '#cf222e' }}>{h.status}</span>
                              <span style={{ color: darkMode ? '#8b949e' : '#57606a' }}>{h.responseTime}ms</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    <div style={{ display: 'flex', gap: '12px', marginBottom: '12px', alignItems: 'center' }}>
                      <input
                        type="text"
                        id={`auth-token-${id}`}
                        placeholder="Bearer Token"
                        value={authToken}
                        onChange={(e) => {
                          setAuthToken(e.target.value);
                          handleInputChange(e, 'header');
                        }}
                        style={{
                          flex: 1,
                          padding: '10px',
                          background: darkMode ? '#0d1117' : '#fff',
                          border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`,
                          borderRadius: '6px',
                          color: darkMode ? '#e6edf3' : '#24292f'
                        }}
                      />
                      <input
                        type="text"
                        id={`api-key-${id}`}
                        placeholder="API Key"
                        value={apiKey}
                        onChange={(e) => {
                          setApiKey(e.target.value);
                          handleInputChange(e, 'header');
                        }}
                        style={{
                          flex: 1,
                          padding: '10px',
                          background: darkMode ? '#0d1117' : '#fff',
                          border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`,
                          borderRadius: '6px',
                          color: darkMode ? '#e6edf3' : '#24292f'
                        }}
                      />
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', whiteSpace: 'nowrap', padding: '10px 16px', background: darkMode ? '#0d1117' : '#f6f8fa', border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`, borderRadius: '6px' }}>
                        <span style={{ fontSize: '13px', fontWeight: '600', color: darkMode ? '#e6edf3' : '#24292f' }}>CORS</span>
                        <div style={{ position: 'relative', width: '44px', height: '24px', cursor: 'pointer' }} onClick={() => setCorsEnabled(!corsEnabled)}>
                          <div style={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            background: corsEnabled ? '#28a745' : (darkMode ? '#30363d' : '#8b949e'),
                            borderRadius: '24px',
                            transition: '0.3s'
                          }}>
                            <div style={{
                              position: 'absolute',
                              height: '18px',
                              width: '18px',
                              left: corsEnabled ? '23px' : '3px',
                              bottom: '3px',
                              background: 'white',
                              borderRadius: '50%',
                              transition: '0.3s'
                            }} />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
                    <button
                      onClick={() => {
                        const shareData = {
                          endpoint: `${method.toUpperCase()} ${path}`,
                          url: replaceVars(url),
                          method: method.toUpperCase(),
                          headers: {},
                          params: {},
                          body: null,
                          files: {}
                        };
                        
                        if (authToken) shareData.headers['Authorization'] = `Bearer ${authToken}`;
                        if (apiKey) shareData.headers['X-API-Key'] = apiKey;
                        
                        if (endpoint.parameters) {
                          endpoint.parameters.forEach(param => {
                            const value = paramValues[`${id}-${param.name}`];
                            if (value) {
                              if (param.in === 'header') shareData.headers[param.name] = replaceVars(value);
                              else shareData.params[param.name] = { in: param.in, value: replaceVars(value) };
                            }
                          });
                        }
                        
                        const isMultipart = endpoint.requestBody?.content?.['multipart/form-data'];
                        if (isMultipart) {
                          const schema = endpoint.requestBody.content['multipart/form-data'].schema;
                          Object.keys(schema?.properties || {}).forEach(fieldName => {
                            const file = fileValues[`${id}-${fieldName}`];
                            const value = paramValues[`${id}-${fieldName}`];
                            if (file) shareData.files[fieldName] = file.name;
                            else if (value) shareData.body = { ...shareData.body, [fieldName]: replaceVars(value) };
                          });
                        } else if (bodyValues[id] !== undefined || samp) {
                          shareData.body = bodyValues[id] !== undefined ? bodyValues[id] : samp;
                        }
                        
                        navigator.clipboard.writeText(JSON.stringify(shareData, null, 2));
                        setModal({
                          type: 'alert',
                          title: 'Request Shared',
                          message: 'Request configuration copied to clipboard!',
                          onConfirm: () => setModal(null)
                        });
                      }}
                      style={{
                        padding: '12px 20px',
                        background: darkMode ? '#21262d' : '#f6f8fa',
                        color: darkMode ? '#e6edf3' : '#24292f',
                        border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`,
                        borderRadius: '8px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        fontSize: '14px',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = darkMode ? '#30363d' : '#eaeef2';
                        e.target.style.transform = 'translateY(-1px)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = darkMode ? '#21262d' : '#f6f8fa';
                        e.target.style.transform = 'translateY(0)';
                      }}
                    >
                      Share Request
                    </button>
                    <button
                      data-send-request
                      onClick={async () => {
                        setLoading({...loading, [id]: true});
                        const startTime = performance.now();
                      try {
                        const isMultipart = endpoint.requestBody?.content?.['multipart/form-data'];
                        const headers = {};
                        if (!isMultipart) headers['Content-Type'] = 'application/json';
                        if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
                        if (apiKey) headers['X-API-Key'] = apiKey;
                        
                        if (endpoint.parameters) {
                          endpoint.parameters.forEach(param => {
                            const value = paramValues[`${id}-${param.name}`];
                            if (param.in === 'header' && value) {
                              headers[param.name] = value;
                            }
                          });
                        }
                        
                        let requestBody;
                        if (isMultipart) {
                          const formData = new FormData();
                          const schema = endpoint.requestBody.content['multipart/form-data'].schema;
                          Object.keys(schema?.properties || {}).forEach(fieldName => {
                            const file = fileValues[`${id}-${fieldName}`];
                            const value = paramValues[`${id}-${fieldName}`];
                            if (file) {
                              formData.append(fieldName, file);
                            } else if (value) {
                              formData.append(fieldName, replaceVars(value));
                            }
                          });
                          requestBody = formData;
                        } else {
                          requestBody = bodyValues[id] !== undefined ? bodyValues[id] : samp;
                          requestBody = replaceVars(requestBody);
                        }
                        
                        const finalHeaders = {};
                        for (const [k, v] of Object.entries(headers)) {
                          finalHeaders[k] = replaceVars(v);
                        }
                        
                        const response = await fetch(buildUrl(), {
                          method: method.toUpperCase(),
                          headers: finalHeaders,
                          body: requestBody || undefined,
                          mode: corsEnabled ? 'cors' : 'no-cors'
                        });
                        
                        const endTime = performance.now();
                        const responseTime = Math.round(endTime - startTime);
                        
                        const contentType = response.headers.get('content-type');
                        let data;
                        if (contentType?.includes('application/json')) {
                          data = await response.json();
                        } else {
                          data = await response.text();
                        }
                        
                        let validationError = null;
                        const expectedSchema = endpoint.responses?.[response.status]?.content?.['application/json']?.schema;
                        if (expectedSchema && typeof data === 'object') {
                          const validateSchema = (schema, data, path = 'root') => {
                            if (schema.$ref) {
                              const ref = schema.$ref.replace('#/components/schemas/', '');
                              schema = spec.components?.schemas?.[ref] || schema;
                            }
                            if (schema.type === 'object' && schema.properties) {
                              for (const [key, propSchema] of Object.entries(schema.properties)) {
                                if (schema.required?.includes(key) && !(key in data)) {
                                  return `Missing required field: ${path}.${key}`;
                                }
                                if (key in data) {
                                  const err = validateSchema(propSchema, data[key], `${path}.${key}`);
                                  if (err) return err;
                                }
                              }
                            }
                            if (schema.type && typeof data !== 'undefined') {
                              const typeMap = { string: 'string', number: 'number', integer: 'number', boolean: 'boolean', object: 'object', array: 'object' };
                              if (typeMap[schema.type] && typeof data !== typeMap[schema.type]) {
                                return `Type mismatch at ${path}: expected ${schema.type}, got ${typeof data}`;
                              }
                            }
                            return null;
                          };
                          validationError = validateSchema(expectedSchema, data);
                        }
                        
                        const result = {
                          status: response.status,
                          statusText: response.statusText,
                          headers: Object.fromEntries(response.headers.entries()),
                          data,
                          responseTime,
                          validationError
                        };
                        
                        setResponses({...responses, [id]: result});
                        
                        // Run test script if exists
                        if (testScripts[id]) {
                          try {
                            const testFunc = new Function('response', 'status', 'data', 'headers', 'responseTime', testScripts[id]);
                            testFunc(result, result.status, result.data, result.headers, result.responseTime);
                            setTestResults({...testResults, [id]: { passed: true, message: 'All tests passed' }});
                          } catch (error) {
                            setTestResults({...testResults, [id]: { passed: false, message: error.message }});
                          }
                        }
                        
                        setHistory([{
                          id: Date.now(),
                          method: method.toUpperCase(),
                          url,
                          timestamp: new Date().toISOString(),
                          status: response.status,
                          responseTime
                        }, ...history]);
                      } catch (error) {
                        setResponses({...responses, [id]: {
                          error: error.message,
                          note: 'CORS error? Make sure the API allows requests from this origin.'
                        }});
                      } finally {
                        setLoading({...loading, [id]: false});
                      }
                    }}
                      disabled={loading[id]}
                      style={{
                        flex: 1,
                        padding: '14px 24px',
                        background: loading[id] ? '#6e7781' : '#0969da',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: loading[id] ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        fontSize: '15px',
                        transition: 'all 0.2s',
                        boxShadow: loading[id] ? 'none' : '0 2px 8px rgba(9, 105, 218, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        if (!loading[id]) {
                          e.target.style.background = '#0860ca';
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(9, 105, 218, 0.4)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!loading[id]) {
                          e.target.style.background = '#0969da';
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 8px rgba(9, 105, 218, 0.3)';
                        }
                      }}
                    >
                      {loading[id] ? 'Sending...' : 'Send Request'}
                    </button>
                    <button
                      onClick={async () => {
                        setModal({
                          type: 'prompt',
                          title: 'Bulk Test',
                          message: 'Number of requests:',
                          defaultValue: '10',
                          onConfirm: async (value) => {
                            setModal(null);
                            const count = parseInt(value);
                            if (!count) return;
                            setLoading({...loading, [id]: true});
                        const results = [];
                        for (let i = 0; i < count; i++) {
                          const start = performance.now();
                          try {
                            const headers = { 'Content-Type': 'application/json' };
                            if (authToken) headers['Authorization'] = `Bearer ${authToken}`;
                            if (apiKey) headers['X-API-Key'] = apiKey;
                            if (endpoint.parameters) {
                              endpoint.parameters.forEach(param => {
                                const value = paramValues[`${id}-${param.name}`];
                                if (param.in === 'header' && value) headers[param.name] = value;
                              });
                            }
                            let requestBody = bodyValues[id] !== undefined ? bodyValues[id] : samp;
                            requestBody = replaceVars(requestBody);
                            const finalHeaders = {};
                            for (const [k, v] of Object.entries(headers)) finalHeaders[k] = replaceVars(v);
                            const response = await fetch(replaceVars(url), {
                              method: method.toUpperCase(),
                              headers: finalHeaders,
                              body: requestBody || undefined,
                              mode: corsEnabled ? 'cors' : 'no-cors'
                            });
                            results.push({ status: response.status, time: Math.round(performance.now() - start) });
                          } catch (e) {
                            results.push({ status: 'error', time: Math.round(performance.now() - start) });
                          }
                        }
                            setLoading({...loading, [id]: false});
                            const avg = results.reduce((sum, r) => sum + r.time, 0) / results.length;
                            const success = results.filter(r => r.status < 300).length;
                            setModal({
                              type: 'alert',
                              title: 'Bulk Test Complete',
                              message: `Total: ${count}\nSuccess: ${success}\nFailed: ${count - success}\nAvg Time: ${avg.toFixed(0)}ms`,
                              onConfirm: () => setModal(null)
                            });
                          },
                          onCancel: () => setModal(null)
                        });
                      }}
                      disabled={loading[id]}
                      style={{
                        padding: '14px 20px',
                        background: loading[id] ? '#6e7781' : '#8250df',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: loading[id] ? 'not-allowed' : 'pointer',
                        fontWeight: '600',
                        fontSize: '14px',
                        whiteSpace: 'nowrap',
                        transition: 'all 0.2s',
                        boxShadow: loading[id] ? 'none' : '0 2px 8px rgba(130, 80, 223, 0.3)'
                      }}
                      onMouseEnter={(e) => {
                        if (!loading[id]) {
                          e.target.style.background = '#7340cf';
                          e.target.style.transform = 'translateY(-1px)';
                          e.target.style.boxShadow = '0 4px 12px rgba(130, 80, 223, 0.4)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!loading[id]) {
                          e.target.style.background = '#8250df';
                          e.target.style.transform = 'translateY(0)';
                          e.target.style.boxShadow = '0 2px 8px rgba(130, 80, 223, 0.3)';
                        }
                      }}
                    >
                      Bulk Test
                    </button>
                  </div>
                  {showComparison && Object.keys(responseComparison).length > 1 && (
                    <div style={{ marginBottom: '20px', padding: '16px', background: darkMode ? '#0d1117' : '#f6f8fa', border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`, borderRadius: '6px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: darkMode ? '#58a6ff' : '#0969da' }}>Response Comparison</h3>
                        <button
                          onClick={() => {
                            setResponseComparison({});
                            setShowComparison(false);
                          }}
                          style={{
                            padding: '4px 8px',
                            background: '#cf222e',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            fontSize: '11px'
                          }}
                        >
                          Clear
                        </button>
                      </div>
                      {Object.entries(responseComparison).map(([key, resp]) => (
                        <div key={key} style={{ marginBottom: '12px', padding: '12px', background: darkMode ? '#161b22' : '#fff', borderRadius: '6px', border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}` }}>
                          <div style={{ fontSize: '12px', marginBottom: '8px' }}>
                            <strong>Status:</strong> <span style={{ color: resp.status < 300 ? '#1a7f37' : '#cf222e' }}>{resp.status}</span> | 
                            <strong> Time:</strong> {resp.responseTime}ms | 
                            <strong> Size:</strong> {JSON.stringify(resp.data).length} bytes
                          </div>
                          <pre style={{ fontSize: '11px', margin: 0, maxHeight: '150px', overflow: 'auto', color: darkMode ? '#e6edf3' : '#24292f' }}>{JSON.stringify(resp.data, null, 2)}</pre>
                        </div>
                      ))}
                    </div>
                  )}
                  {responses[id] && (
                    <>
                      <h3 style={{ fontSize: '16px', fontWeight: 700, marginBottom: '16px', color: darkMode ? '#58a6ff' : '#0969da', letterSpacing: '-0.3px' }}>Response</h3>
                      {responses[id].error ? (
                        <div style={{ padding: '16px', background: darkMode ? '#1a1f2e' : '#fff5f5', border: `1px solid ${darkMode ? '#cf222e' : '#ff8182'}`, borderRadius: '8px', color: '#cf222e' }}>
                          <strong>Error:</strong> {responses[id].error}
                          {responses[id].note && <div style={{ marginTop: '8px', fontSize: '13px' }}>{responses[id].note}</div>}
                        </div>
                      ) : (
                        <>
                          <div style={{ marginBottom: '16px', padding: '16px', background: darkMode ? '#0d1117' : '#f6f8fa', borderRadius: '8px', border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}` }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: responses[id].validationError ? '8px' : '0' }}>
                              <div>
                                <strong>Status:</strong> <span style={{ color: responses[id].status < 300 ? '#1a7f37' : '#cf222e' }}>{responses[id].status} {responses[id].statusText}</span>
                              </div>
                              <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                                {responses[id].responseTime && (
                                  <div style={{ fontSize: '13px', color: darkMode ? '#8b949e' : '#57606a' }}>
                                    <strong>Time:</strong> {responses[id].responseTime}ms
                                  </div>
                                )}
                                <button
                                  onClick={() => {
                                    const shareData = {
                                      endpoint: `${method.toUpperCase()} ${path}`,
                                      url,
                                      params: paramValues,
                                      body: bodyValues[id],
                                      response: responses[id]
                                    };
                                    navigator.clipboard.writeText(JSON.stringify(shareData, null, 2));
                                    setModal({
                                      type: 'alert',
                                      title: 'Success',
                                      message: 'Request copied to clipboard!',
                                      onConfirm: () => setModal(null)
                                    });
                                  }}
                                  style={{
                                    padding: '4px 8px',
                                    background: '#0969da',
                                    color: '#fff',
                                    border: 'none',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    fontSize: '11px',
                                    fontWeight: '600'
                                  }}
                                >
                                  Share
                                </button>
                              </div>
                            </div>
                            {responses[id].validationError && (
                              <div style={{ padding: '10px 12px', background: darkMode ? '#1a1f2e' : '#fff5f5', border: `1px solid #cf222e`, borderRadius: '6px', color: '#cf222e', fontSize: '13px' }}>
                                <strong>⚠️ Validation Error:</strong> {responses[id].validationError}
                              </div>
                            )}
                            {!responses[id].validationError && responses[id].status < 300 && (
                              <div style={{ padding: '10px 12px', background: darkMode ? '#0d1f17' : '#dafbe1', border: `1px solid #1a7f37`, borderRadius: '6px', color: '#1a7f37', fontSize: '13px', marginTop: '8px' }}>
                                ✓ Response matches schema
                              </div>
                            )}
                          </div>
                          <div style={{ marginBottom: '12px' }}>
                            <input
                              type="text"
                              placeholder="JSONPath query (e.g., $.data.id or data.users[0].name)"
                              id={`jsonpath-${id}`}
                              style={{
                                width: '100%',
                                padding: '8px',
                                background: darkMode ? '#0d1117' : '#fff',
                                border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`,
                                borderRadius: '6px',
                                color: darkMode ? '#e6edf3' : '#24292f',
                                fontSize: '13px',
                                marginBottom: '8px'
                              }}
                            />
                            <button
                              onClick={() => {
                                const path = document.getElementById(`jsonpath-${id}`).value;
                                if (path && typeof responses[id].data === 'object') {
                                  try {
                                    const parts = path.replace(/^\$\./, '').split('.');
                                    let result = responses[id].data;
                                    for (const part of parts) {
                                      const match = part.match(/(\w+)\[(\d+)\]/);
                                      if (match) {
                                        result = result[match[1]][parseInt(match[2])];
                                      } else {
                                        result = result[part];
                                      }
                                    }
                                    setModal({
                                      type: 'alert',
                                      title: 'JSONPath Result',
                                      message: JSON.stringify(result, null, 2),
                                      onConfirm: () => setModal(null)
                                    });
                                  } catch (e) {
                                    setModal({
                                      type: 'alert',
                                      title: 'Error',
                                      message: e.message,
                                      onConfirm: () => setModal(null)
                                    });
                                  }
                                }
                              }}
                              style={{
                                padding: '6px 12px',
                                background: '#0969da',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '600'
                              }}
                            >
                              Extract Value
                            </button>
                          </div>
                          <CodeBlock $darkMode={darkMode}>
                            <CodeHeader $darkMode={darkMode}>
                              <span>Response Body</span>
                              <CopyBtn $copied={copied['resp'+id]} onClick={() => copy('resp'+id, typeof responses[id].data === 'string' ? responses[id].data : JSON.stringify(responses[id].data, null, 2))}>
                                {copied['resp'+id] ? 'Copied!' : 'Copy'}
                              </CopyBtn>
                            </CodeHeader>
                            <Pre $darkMode={darkMode}>{typeof responses[id].data === 'string' ? responses[id].data : JSON.stringify(responses[id].data, null, 2)}</Pre>
                          </CodeBlock>
                        </>
                      )}
                    </>
                  )}
                </>
              )}

              {tab === 'code' && (
                <>
                  <CodeBlock $darkMode={darkMode}>
                    <CodeHeader $darkMode={darkMode}>
                      <span>cURL</span>
                      <CopyBtn $copied={copied['c'+id]} onClick={() => copy('c'+id, curl)}>{copied['c'+id] ? 'Copied!' : 'Copy'}</CopyBtn>
                    </CodeHeader>
                    <Pre $darkMode={darkMode}>{curl}</Pre>
                  </CodeBlock>
                  <CodeBlock $darkMode={darkMode}>
                    <CodeHeader $darkMode={darkMode}>
                      <span>JavaScript</span>
                      <CopyBtn $copied={copied['j'+id]} onClick={() => copy('j'+id, js)}>{copied['j'+id] ? 'Copied!' : 'Copy'}</CopyBtn>
                    </CodeHeader>
                    <Pre $darkMode={darkMode}>{js}</Pre>
                  </CodeBlock>
                  <CodeBlock $darkMode={darkMode}>
                    <CodeHeader $darkMode={darkMode}>
                      <span>Python</span>
                      <CopyBtn $copied={copied['p'+id]} onClick={() => copy('p'+id, py)}>{copied['p'+id] ? 'Copied!' : 'Copy'}</CopyBtn>
                    </CodeHeader>
                    <Pre $darkMode={darkMode}>{py}</Pre>
                  </CodeBlock>
                  <CodeBlock $darkMode={darkMode}>
                    <CodeHeader $darkMode={darkMode}>
                      <span>Go</span>
                      <CopyBtn $copied={copied['g'+id]} onClick={() => copy('g'+id, go)}>{copied['g'+id] ? 'Copied!' : 'Copy'}</CopyBtn>
                    </CodeHeader>
                    <Pre $darkMode={darkMode}>{go}</Pre>
                  </CodeBlock>
                  <CodeBlock $darkMode={darkMode}>
                    <CodeHeader $darkMode={darkMode}>
                      <span>Ruby</span>
                      <CopyBtn $copied={copied['r'+id]} onClick={() => copy('r'+id, ruby)}>{copied['r'+id] ? 'Copied!' : 'Copy'}</CopyBtn>
                    </CodeHeader>
                    <Pre $darkMode={darkMode}>{ruby}</Pre>
                  </CodeBlock>
                  <CodeBlock $darkMode={darkMode}>
                    <CodeHeader $darkMode={darkMode}>
                      <span>PHP</span>
                      <CopyBtn $copied={copied['ph'+id]} onClick={() => copy('ph'+id, php)}>{copied['ph'+id] ? 'Copied!' : 'Copy'}</CopyBtn>
                    </CodeHeader>
                    <Pre $darkMode={darkMode}>{php}</Pre>
                  </CodeBlock>
                  <CodeBlock $darkMode={darkMode}>
                    <CodeHeader $darkMode={darkMode}>
                      <span>Java</span>
                      <CopyBtn $copied={copied['jv'+id]} onClick={() => copy('jv'+id, java)}>{copied['jv'+id] ? 'Copied!' : 'Copy'}</CopyBtn>
                    </CodeHeader>
                    <Pre $darkMode={darkMode}>{java}</Pre>
                  </CodeBlock>
                </>
              )}
            </Endpoint>
          );
        })}
        
        {schemas.length > 0 && (
          <>
            <h2 style={{ fontSize: '2em', marginTop: '60px', marginBottom: '30px', color: darkMode ? '#58a6ff' : '#0969da' }}>Schemas</h2>
            {schemas.map(schemaName => {
              const schema = spec.components.schemas[schemaName];
              return (
                <Endpoint key={schemaName} id={'schema-' + schemaName} $darkMode={darkMode}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
                    <span style={{ 
                      fontSize: '11px', 
                      fontWeight: '700', 
                      padding: '6px 12px', 
                      borderRadius: '6px', 
                      background: darkMode ? '#30363d' : '#e1e4e8',
                      color: darkMode ? '#8b949e' : '#57606a',
                      letterSpacing: '0.5px'
                    }}>SCHEMA</span>
                    <span style={{ fontFamily: 'monospace', fontSize: '20px', fontWeight: '700' }}>{schemaName}</span>
                  </div>
                  {schema.description && <p style={{ color: darkMode ? '#8b949e' : '#57606a', marginBottom: '20px' }}>{schema.description}</p>}
                  <CodeBlock $darkMode={darkMode}>
                    <CodeHeader $darkMode={darkMode}>
                      <span>Schema Definition</span>
                      <CopyBtn $copied={copied['schema-'+schemaName]} onClick={() => copy('schema-'+schemaName, JSON.stringify(schema, null, 2))}>
                        {copied['schema-'+schemaName] ? 'Copied!' : 'Copy'}
                      </CopyBtn>
                    </CodeHeader>
                    <Pre>{JSON.stringify(schema, null, 2)}</Pre>
                  </CodeBlock>
                </Endpoint>
              );
            })}
          </>
        )}
      </Main>
      {modal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}>
          <div style={{
            background: darkMode ? '#161b22' : '#fff',
            border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`,
            borderRadius: '12px',
            padding: '24px',
            minWidth: '400px',
            maxWidth: '600px',
            boxShadow: '0 16px 48px rgba(0,0,0,0.5)'
          }}>
            <h3 style={{ margin: '0 0 16px 0', fontSize: '18px', fontWeight: '700', color: darkMode ? '#e6edf3' : '#24292f' }}>{modal.title}</h3>
            <div style={{ marginBottom: '20px', fontSize: '14px', color: darkMode ? '#8b949e' : '#57606a', whiteSpace: 'pre-wrap' }}>{modal.message}</div>
            {modal.type === 'prompt' && (
              <input
                type="text"
                defaultValue={modal.defaultValue || ''}
                id="modal-input"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    modal.onConfirm(document.getElementById('modal-input').value);
                  }
                }}
                style={{
                  width: '100%',
                  padding: '10px',
                  background: darkMode ? '#0d1117' : '#fff',
                  border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`,
                  borderRadius: '6px',
                  color: darkMode ? '#e6edf3' : '#24292f',
                  fontSize: '14px',
                  marginBottom: '16px'
                }}
              />
            )}
            <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
              {modal.type === 'prompt' && (
                <button
                  onClick={modal.onCancel}
                  style={{
                    padding: '8px 16px',
                    background: darkMode ? '#21262d' : '#f6f8fa',
                    color: darkMode ? '#e6edf3' : '#24292f',
                    border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`,
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontWeight: '600',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
              )}
              <button
                onClick={() => {
                  if (modal.type === 'prompt') {
                    modal.onConfirm(document.getElementById('modal-input').value);
                  } else {
                    modal.onConfirm();
                  }
                }}
                style={{
                  padding: '8px 16px',
                  background: '#0969da',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '14px'
                }}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}
      {autocomplete.show && (
        <div style={{
          position: 'absolute',
          top: autocomplete.position.top,
          left: autocomplete.position.left,
          background: darkMode ? '#161b22' : '#fff',
          border: `1px solid ${darkMode ? '#30363d' : '#d0d7de'}`,
          borderRadius: '6px',
          boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
          zIndex: 10001,
          maxHeight: '200px',
          overflowY: 'auto',
          minWidth: '200px'
        }}>
          {autocomplete.items.map((item, idx) => (
            <div
              key={idx}
              onClick={() => {
                const input = document.getElementById(autocomplete.inputId);
                if (input) {
                  const cursorPos = input.selectionStart;
                  const text = input.value;
                  const beforeCursor = text.substring(0, cursorPos);
                  const afterCursor = text.substring(cursorPos);
                  const lastWord = beforeCursor.match(/{{(\w*)$/)?.[1] || beforeCursor.match(/(\S*)$/)?.[0] || '';
                  const newValue = beforeCursor.substring(0, beforeCursor.length - lastWord.length - (beforeCursor.endsWith('{{') ? 2 : 0)) + item + afterCursor;
                  input.value = newValue;
                  input.dispatchEvent(new Event('input', { bubbles: true }));
                }
                setAutocomplete({ show: false, items: [], position: { top: 0, left: 0 }, selected: 0, inputId: null });
              }}
              style={{
                padding: '8px 12px',
                cursor: 'pointer',
                background: idx === autocomplete.selected ? (darkMode ? '#21262d' : '#f6f8fa') : 'transparent',
                color: darkMode ? '#e6edf3' : '#24292f',
                fontSize: '13px',
                fontFamily: item.startsWith('{{') ? 'monospace' : 'inherit'
              }}
              onMouseEnter={() => setAutocomplete(prev => ({ ...prev, selected: idx }))}
            >
              {item}
            </div>
          ))}
        </div>
      )}
    </Layout>
  );
}

export default CustomApiViewer;
